import React, { useEffect, useState } from 'react';
import { useAuth, useFeature, usePermission } from '../context/AuthContext';

/**
 * The employee navigation destinations, in one place.
 *
 * Shared by the Sidebar and the TopBar account menu. On a phone (<=600px) the
 * sidebar is display:none and the account menu is the only navigation, so
 * both must offer exactly the same flag- and permission-gated destinations;
 * two hand-kept lists drifted (the phone menu had only Home and My profile).
 */

const svgProps = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', 'aria-hidden': 'true' };

export const NavIcons = {
  home: () => (
    <svg {...svgProps}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
      <path d="M10 21v-6h4v6" />
    </svg>
  ),
  tools: () => (
    <svg {...svgProps}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  people: () => (
    <svg {...svgProps}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  requests: () => (
    <svg {...svgProps}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  buildApp: () => (
    <svg {...svgProps}>
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
    </svg>
  ),
  resultsChain: () => (
    <svg {...svgProps}>
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="6" r="2" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="18" cy="18" r="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="6" y1="8" x2="6" y2="16" />
      <line x1="18" y1="8" x2="18" y2="16" />
      <line x1="8" y1="18" x2="16" y2="18" />
    </svg>
  ),
  orgChart: () => (
    <svg {...svgProps}>
      <rect x="9" y="2" width="6" height="5" rx="1" />
      <rect x="2" y="16" width="6" height="5" rx="1" />
      <rect x="16" y="16" width="6" height="5" rx="1" />
      <path d="M12 7v5" />
      <path d="M5 16v-2h14v2" />
    </svg>
  ),
  ontology: () => (
    <svg {...svgProps}>
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
      <line x1="9" y1="7" x2="16" y2="7" />
      <line x1="9" y1="11" x2="14" y2="11" />
    </svg>
  ),
  vendors: () => (
    <svg {...svgProps}>
      <path d="M3 7h13v10H3z" />
      <path d="M16 10h3.5L22 13v4h-6" />
      <circle cx="7.5" cy="18" r="1.8" />
      <circle cx="18" cy="18" r="1.8" />
    </svg>
  ),
  departments: () => (
    <svg {...svgProps}>
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 9h.01M15 9h.01M9 12h.01M15 12h.01" />
    </svg>
  ),
};

// Must match the 601-900px block in styles.css that shrinks the sidebar to an
// icon-only rail (labels clipped, still in the accessibility tree).
const ICON_RAIL_QUERY = '(min-width: 601px) and (max-width: 900px)';

function matchRail() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia(ICON_RAIL_QUERY).matches;
}

/**
 * True while the sidebar is the 601-900px icon-only rail. The sidebar gives
 * its links a native `title` only then (and when collapsed): with the labels
 * visible a title just repeats them, and over the collapsed flyout it doubled
 * up. No matchMedia (jsdom, very old browsers) reads as false.
 */
export function useIconRail() {
  const [rail, setRail] = useState(matchRail);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(ICON_RAIL_QUERY);
    const onChange = () => setRail(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return rail;
}

/**
 * {
 *   home: { to, label|null (null until the flag map loads), Icon },
 *   items: the gated destinations after Home, in sidebar order,
 *   isDeptManager: whether the My departments group/link applies,
 * }
 * Each item: { key, to, label, Icon, isActive(pathname) }.
 */
export default function useNavItems() {
  const { user, featuresLoaded } = useAuth() || {};
  const canSeeResultsChain = usePermission('results_chain.view');
  const canSeeOrgChart = usePermission('org_chart.view');
  const canSeeOntology = usePermission('business_ontology.view');
  const buildAppEnabled = useFeature('build_your_app');
  const orgChartEnabled = useFeature('org_chart');
  const vendorsEnabled = useFeature('vendor_management');
  const homeOn = useFeature('home_dashboard');
  const isDeptManager = (user?.roles || []).includes('dept_manager');
  // Flags fail open, so before they load homeOn reads true; naming the link
  // now would show "Home" and flip to "My tools" for an org that turned the
  // home page off. label is null until the map arrives.
  const homeLabelKnown = featuresLoaded !== false;

  const exact = (path) => (pathname) => pathname === path;
  const items = [
    { key: 'people', to: '/people', label: 'Employee Directory', Icon: NavIcons.people, isActive: exact('/people') },
    { key: 'requests', to: '/requests', label: isDeptManager ? 'Tool requests' : 'My tool requests', Icon: NavIcons.requests, isActive: exact('/requests') },
    buildAppEnabled && { key: 'build-app', to: '/build-app', label: 'Build an app', Icon: NavIcons.buildApp, isActive: (p) => p === '/build-app' || p.startsWith('/build-app/') },
    canSeeResultsChain && { key: 'results-chain', to: '/results-chain', label: 'Results Chain', Icon: NavIcons.resultsChain, isActive: exact('/results-chain') },
    canSeeOrgChart && orgChartEnabled && { key: 'org-chart', to: '/org-chart', label: 'Org Chart', Icon: NavIcons.orgChart, isActive: exact('/org-chart') },
    // has_vendor_access is a nav hint from /api/me; the server re-checks every request.
    vendorsEnabled && user?.has_vendor_access && { key: 'vendors', to: '/vendors', label: 'Vendors', Icon: NavIcons.vendors, isActive: (p) => p === '/vendors' || p.startsWith('/vendors/') },
    canSeeOntology && { key: 'ontology', to: '/business-ontology', label: 'Business Ontology', Icon: NavIcons.ontology, isActive: exact('/business-ontology') },
  ].filter(Boolean);

  return {
    home: {
      to: '/',
      label: homeLabelKnown ? (homeOn ? 'Home' : 'My tools') : null,
      Icon: homeOn ? NavIcons.home : NavIcons.tools,
      isActive: exact('/'),
    },
    items,
    isDeptManager,
  };
}
